/**
 todo: handle radio buttons (validation) as a group instead of separate form-items
 */

import Observer from 'utils/Observer';
import DOMHelper from 'utils/DOMHelpers';

const DATA_FILTER_ATTR = '';
const DATA_VALID_ATTR = 'aria-invalid';
const DATA_VALID_VAL = 'false';
const DATA_INVALID_VAL = 'true';
const DATA_MATCHES_ATTR = 'data-matches';
const DATA_ERROR_ATTR = 'data-val-regex';
const DATA_ERROR_CLASS = 'error-message';
const DATA_REQUIRED_ATTR = 'data-val-required';

/**
 * * Class FormItem that handles base functionality for .....
 * @class
 */
class FormItem {

    /**
     * FormItem.
     * @constructs
     * @param {HTMLElement} element - The HTMLElement this module is constructed upon
     * @param {Object} options - ConditionerJS's merged options
     */
    constructor(element, options) {

        this._element = element;
        this._options = options;

        this._initialize();

    }

    /**
     * Initialize form-item component
     * @static
     * @private
     */
    _initialize() {

        this._label = null;

        // Has an existing error label
        if (this._element.nextElementSibling && this._element.nextElementSibling.classList.contains(DATA_ERROR_CLASS)) {
            this._label = this._element.nextElementSibling;
        }

        this._validateRegex = new RegExp(this._element.getAttribute('pattern'));
        if (this._element.hasAttribute(DATA_FILTER_ATTR)) {
            this._filterRegex = new RegExp(this._element.getAttribute(DATA_FILTER_ATTR), 'g');
        }

        // Element to match value against
        this._matchingElement = this._element.hasAttribute(DATA_MATCHES_ATTR) ? document.getElementById(this._element.getAttribute(DATA_MATCHES_ATTR)) : null;

        // Messages
        this._errorMessage = this._element.getAttribute(DATA_ERROR_ATTR);
        this._requiredMessage = this._element.getAttribute(DATA_REQUIRED_ATTR) || 'Verplicht veld';

        // Bind events
        this._bindEvents();

    }

    /**
     * Bind events for the module
     * @static
     * @private
     */
    _bindEvents() {

        this._element.addEventListener('blur', e => {
            this._onChange(e);
        });
        this._element.addEventListener('input', e => this._onInput(e));

        // Also bind to the element we match against
        this._matchingElement && this._matchingElement.addEventListener('input', this._onMatchingElementInput.bind(this));

    }

    /**
     * Validate this form-item
     * @static
     * @private
     */
    _onChange() {
        this.validate();
        Observer.publish(this, 'change');
    }

    /**
     * Validate this form-item (only to valid, not invalid)
     * @static
     * @private
     */
    _onInput() {
        this.isValid() && this.setValid();
        Observer.publish(this, 'change');
    }

    /**
     * Validate this form-item when the matching element input changes
     * @static
     * @public
     */
    _onMatchingElementInput() {
        this._element.value && this.validate();
    }

    /**
     * Public function to set the validation status of this form-item
     * @static
     * @public
     */
    validate() {
        if (this.isIgnored()) {
            return;
        }
        this.isValid() ? this.setValid() : this.setInvalid();
    }

    /**
     * Checks this form item for its validity
     * @returns {Boolean} Valid state of this form item
     * @static
     * @public
     */
    isValid() {

        if (this._filterRegex) {
            // When a filter regex is given, filter first then check the validate regex
            let filteredValue = this._element.value.replace(this._filterRegex, '');

            return this._validateRegex.test(filteredValue);
        }

        if (this._matchingElement) {
            return this._element.value === this._matchingElement.value && this._element.value !== '';
        }

        if (this._element.type === 'checkbox') {
            return this._element.checked;
        }

        // Return the html5 form item valid state

        return this._element.checkValidity();

    }

    /**
     * Whether this input should be ignored when validating the form, because it's not required AND empty
     * @returns {Boolean} Required
     * @static
     * @public
     */
    isIgnored() {
        return this._element.value === '' && !this._element.hasAttribute('required');
    }

    /**
     * Set state of this form-item
     * @static
     * @public
     */
    setValid() {

        if (!this._label && this._element.type === 'radio') {
            this._label = this._getRadioErrorLabel();
        }

        if (this._label) {

            if (this._label.parentNode) {
                this._label.parentNode.removeChild(this._label);
            }

            this._label = null;

        }

        if (this._element.value) {
            // We entered a valid value
            this._element.setAttribute(DATA_VALID_ATTR, DATA_VALID_VAL);
        } else {
            // We entered nothing, but the field wasn't required
            this._element.removeAttribute(DATA_VALID_ATTR);
        }

    }

    /**
     * Set invalid state of this form-item
     * @static
     * @public
     */
    setInvalid() {

        let error;

        // Set element state to invalid
        this._element.setAttribute(DATA_VALID_ATTR, DATA_INVALID_VAL);

        // Empty && invalid -> Fields is not filled, required?
        // Just invalid -> Format doesn't match requested format
        if (this._element.type !== 'checkbox' && this._element.type !== 'radio' && this._element.value) {
            error = this._errorMessage;
        } else if (this._element.hasAttribute('required')) {
            error = this._errorMessage || this._requiredMessage;
        }
        // If our error message is empty, don't attach an error element
        error && this._setErrorMessage(error);

    }

    /**
     * Retrieve the error label for a radio type input (grouped)
     * @returns {Element} - element
     * @private
     */
    _getRadioErrorLabel() {
        let parent = DOMHelper.closest(this._element, '.form-items-radio');
        let returnParent;

        if (parent) {
            returnParent = parent.querySelector(`.${DATA_ERROR_CLASS}`);
        }

        return returnParent;
    }

    /**
     * Update or create the error message
     * @param {String} error - The error message to display
     * @private
     *
     */
    _setErrorMessage(error) {

        if (!this._label && this._element.type === 'radio') {
            this._label = this._getRadioErrorLabel();
        }

        // Update the existing error element, if there is one
        if (this._label) {
            this._label.textContent = error;
            // The bouncer

            return;
        }

        // Create a new error element
        this._label = document.createElement('label');
        this._label.setAttribute('for', this._element.getAttribute('id'));
        this._label.className = DATA_ERROR_CLASS;
        this._label.textContent = error;

        // Append it
        if (this._element.type === 'checkbox') {
            this._element.parentNode.appendChild(this._label);
        } else if (this._element.type === 'radio') {
            DOMHelper.closest(this._element, '.form-layout-item').appendChild(this._label);
        } else {
            DOMHelper.insertAfter(this._label, this._element);
        }
    }

    /**
     * Get the element for this form-item.
     * @return {Element} - element
     * @public
     *
     */
    getItem() {
        return this._element;
    }

}

export default FormItem;
